import paystackConfig, { toKobo, toNaira, formatNaira, validateAmount } from '../config/paystack.config';
import { getUser } from '../utils/auth';

// API Base URL - should match your backend server
const API_URL = process.env.REACT_APP_API_URL;

/**
 * Paystack Payment Service
 * Handles all Paystack payment operations for BestieConnect
 */

class PaystackService {
  constructor() {
    this.config = paystackConfig;
    this.scriptLoaded = false;
  }

  /**
   * Load Paystack Inline script
   */
  loadPaystackScript() {
    return new Promise((resolve, reject) => {
      if (this.scriptLoaded || window.PaystackPop) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://js.paystack.co/v1/inline.js';
      script.async = true;
      
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Paystack script'));
      };

      document.body.appendChild(script);
    });
  }

  /**
   * Initialize Paystack payment
   * @param {Object} paymentData - Payment information
   * @returns {Promise} Payment result
   */
  async initializePayment(paymentData) {
    try {
      // Ensure Paystack script is loaded
      await this.loadPaystackScript();

      // Get current user
      const user = getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Validate amount
      const validation = validateAmount(paymentData.amount);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      // Prepare payment configuration
      const config = this.buildPaymentConfig(paymentData, user);

      // Initialize Paystack
      return new Promise((resolve, reject) => {
        const handler = window.PaystackPop.setup({
          ...config,
          onClose: () => {
            reject(new Error('Payment cancelled by user'));
          },
          callback: (response) => {
            resolve(response);
          },
        });

        handler.openIframe();
      });
    } catch (error) {
      console.error('Paystack initialization error:', error);
      throw error;
    }
  }

  /**
   * Build Paystack payment configuration
   */
  buildPaymentConfig(paymentData, user) {
    const {
      amount,
      email,
      causeId,
      causeName,
      reference,
      metadata = {},
    } = paymentData;

    return {
      key: this.config.publicKey,
      email: email || user.email,
      amount: toKobo(amount), // Convert to kobo
      currency: this.config.currency,
      ref: reference || this.generateReference(),
      channels: this.config.channels,
      
      // Metadata
      metadata: {
        ...this.config.metadata,
        ...metadata,
        user_id: user.id,
        user_email: user.email,
        cause_id: causeId,
        cause_name: causeName,
        donation_amount: amount,
        custom_fields: [
          {
            display_name: 'Cause',
            variable_name: 'cause_name',
            value: causeName,
          },
          {
            display_name: 'Donor Name',
            variable_name: 'donor_name',
            value: user.full_name || user.email,
          },
        ],
      },

      // UI Customization
      label: this.config.ui.title,
      ...this.config.ui.theme,
    };
  }

  /**
   * Generate unique payment reference
   */
  generateReference() {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000000);
    return `BESTIE-DN-${timestamp}-${random}`;
  }

  /**
   * Verify payment with backend
   * @param {string} reference - Payment reference
   * @returns {Promise} Verification result
   */
  async verifyPayment(reference) {
    try {
      // This should call your backend API to verify the payment
      // The backend will verify with Paystack's server
      const response = await fetch(`${API_URL}/donations/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({ reference }),
      });

      if (!response.ok) {
        throw new Error('Payment verification failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Payment verification error:', error);
      throw error;
    }
  }

  /**
   * Process complete donation flow
   * @param {Object} donationData - Donation information
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  async processDonation(donationData, onSuccess, onError) {
    try {
      // Initialize payment
      const paymentResponse = await this.initializePayment(donationData);
      
      if (paymentResponse.status === 'success') {
        // Verify payment
        const verificationResult = await this.verifyPayment(paymentResponse.reference);
        
        if (verificationResult.success) {
          onSuccess && onSuccess(verificationResult);
          return verificationResult;
        } else {
          throw new Error('Payment verification failed');
        }
      } else {
        throw new Error('Payment was not successful');
      }
    } catch (error) {
      console.error('Donation processing error:', error);
      onError && onError(error);
      throw error;
    }
  }

  /**
   * Create subscription plan (for recurring donations)
   * @param {Object} planData - Subscription plan details
   */
  async createSubscriptionPlan(planData) {
    if (!this.config.features.subscriptions) {
      throw new Error('Subscriptions are not enabled');
    }

    // This would call your backend to create a plan with Paystack
    // Implementation depends on your backend structure
    console.log('Creating subscription plan:', planData);
  }

  /**
   * Get payment channels
   */
  getPaymentChannels() {
    return this.config.channels;
  }

  /**
   * Get preset amounts
   */
  getPresetAmounts() {
    return this.config.presetAmounts;
  }

  /**
   * Format currency
   */
  formatCurrency(amount) {
    return formatNaira(amount);
  }

  /**
   * Validate donation amount
   */
  validateDonationAmount(amount) {
    return validateAmount(amount);
  }

  /**
   * Get minimum amount
   */
  getMinAmount() {
    return this.config.minAmount;
  }

  /**
   * Get maximum amount
   */
  getMaxAmount() {
    return this.config.maxAmount;
  }
}

// Export singleton instance
const paystackService = new PaystackService();
export default paystackService;
